import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Upload, TrendingUp, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';
import MobileNav from '@/components/MobileNav';

function ChartAnalyzer() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setSelectedImage(file);
      setError(null);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedImage) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('chart', selectedImage);
      formData.append('language', language); // Envoyer la langue de l'utilisateur
      
      console.log('📊 Chart Analyzer - Language:', language); // Debug
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/analyze-chart`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Analysis failed');
      }
      
      const data = await response.json();
      console.log('✅ Analysis received:', data.trend, data.recommendation); // Debug
      setAnalysis(data);
    } catch (err) {
      setError(`Failed to analyze chart: ${err.message}`);
      console.error('Chart analysis error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedImage(null);
    setPreview(null);
    setAnalysis(null);
    setError(null);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#ff6b35] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Authentication Required</h2>
          <p className="text-gray-400 mb-6">Please log in to use the chart analyzer</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#ff6b35] text-white px-6 py-3 rounded-lg hover:bg-[#ff8555] transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <MobileNav currentPage="chart-analyzer" />
      
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <TrendingUp className="w-8 h-8 text-[#ff6b35]" />
          <h1 className="text-4xl font-bold gradient-text">AI Chart Analyzer</h1>
        </div>

        <div className="bg-[#1a1a1a] border border-[#ff6b35]/20 rounded-xl p-8">
          {!preview ? (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">{t('uploadCapture')}</h2>
                <p className="text-gray-400 text-sm">{t('uploadDesc')}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <button onClick={() => fileInputRef.current?.click()}
                  className="bg-gradient-to-br from-[#ff6b35]/20 to-[#ff6b35]/5 border-2 border-[#ff6b35]/30 rounded-xl p-8 hover:border-[#ff6b35]/60 transition-all group">
                  <Upload className="w-12 h-12 text-[#ff6b35] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold text-white mb-2">{t('uploadFile')}</h3>
                  <p className="text-sm text-gray-400">{t('uploadFileDesc')}</p>
                </button>
                <button onClick={() => cameraInputRef.current?.click()}
                  className="bg-gradient-to-br from-[#ff6b35]/20 to-[#ff6b35]/5 border-2 border-[#ff6b35]/30 rounded-xl p-8 hover:border-[#ff6b35]/60 transition-all group">
                  <Camera className="w-12 h-12 text-[#ff6b35] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold text-white mb-2">{t('takePhoto')}</h3>
                  <p className="text-sm text-gray-400">{t('takePhotoDesc')}</p>
                </button>
              </div>

              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />

              {/* What the AI will analyze */}
              <div className="mt-6 p-5 bg-[#ff6b35]/5 border border-[#ff6b35]/20 rounded-xl">
                <h3 className="text-sm font-bold text-[#ff6b35] mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  {t('whatAIAnalyzes')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { icon: '📈', label: t('trendDetection'), desc: t('trendDetectionDesc') },
                    { icon: '🎯', label: t('supportResistance'), desc: t('supportResistanceDesc') },
                    { icon: '⚡', label: t('breakoutProbability'), desc: t('breakoutDesc') },
                    { icon: '🔥', label: t('momentum'), desc: t('momentumDesc') },
                    { icon: '🧩', label: t('patternRecognition'), desc: t('patternDesc') },
                    { icon: '🛡️', label: t('riskAssessment'), desc: t('riskDesc') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 bg-[#0a0a0a]/60 rounded-lg">
                      <span className="text-lg flex-shrink-0">{item.icon}</span>
                      <div>
                        <div className="text-xs font-semibold text-white">{item.label}</div>
                        <div className="text-xs text-gray-500">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500">{t('supportedCharts')}</span>
                  {['Stock charts', 'Crypto charts', 'Forex', 'Indices', 'Candlestick', 'Line charts'].map(s => (
                    <span key={s} className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full">{s}</span>
                  ))}
                </div>
              </div>

              <p className="text-xs text-center text-gray-600">{t('chartFormat')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Preview */}
              <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
                <img src={preview} alt="Chart preview" className="max-w-full h-auto mx-auto rounded-lg" style={{ maxHeight: '400px' }} />
              </div>

              {/* Action Buttons */}
              {!analysis && (
                <div className="flex gap-4">
                  <button
                    onClick={handleAnalyze}
                    disabled={loading}
                    className="flex-1 bg-[#ff6b35] text-white py-4 rounded-lg hover:bg-[#ff8555] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t('analyzing')}
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-5 h-5" />
                        {t('analyzeChart')}
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleReset}
                    disabled={loading}
                    className="px-6 bg-gray-700 text-white py-4 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    {language === 'fr' ? 'Annuler' : language === 'es' ? 'Cancelar' : 'Cancel'}
                  </button>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Analysis Results */}
              {analysis && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-[#ff6b35]/20 to-[#ff6b35]/5 border border-[#ff6b35]/30 rounded-xl p-6">
                    <h3 className="text-2xl font-bold text-white mb-4">{t('analysisResults')}</h3>
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-[#ff6b35] mb-2">{t('trend')}</h4>
                      <p className="text-white text-lg">{analysis.trend}</p>
                    </div>
                    {analysis.key_levels && (
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-[#ff6b35] mb-2">{t('keyLevels')}</h4>
                        <div className="space-y-2">
                          {analysis.key_levels.support && (
                            <p className="text-gray-300"><strong>{t('support')}:</strong> {analysis.key_levels.support}</p>
                          )}
                          {analysis.key_levels.resistance && (
                            <p className="text-gray-300"><strong>{t('resistance')}:</strong> {analysis.key_levels.resistance}</p>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-[#ff6b35] mb-2">{t('recommendation')}</h4>
                      <p className="text-white text-lg font-semibold">{analysis.recommendation}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-[#ff6b35] mb-2">{t('detailedAnalysis')}</h4>
                      <p className="text-gray-300 leading-relaxed whitespace-pre-line">{analysis.analysis}</p>
                    </div>
                  </div>
                  <div className="bg-[#ff6b35]/10 border border-[#ff6b35]/30 rounded-lg p-4">
                    <p className="text-sm text-gray-300">
                      <strong className="text-[#ff6b35]">⚠️</strong> {t('chartDisclaimer')}
                    </p>
                  </div>
                  <button onClick={handleReset} className="w-full bg-gray-700 text-white py-4 rounded-lg hover:bg-gray-600 transition-colors">
                    {t('analyzeAnother')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChartAnalyzer;